import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Save, Search, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const CRT_OPTIONS = [
  { value: "1", label: "1 - Simples Nacional" },
  { value: "2", label: "2 - Simples Nacional - Excesso de Sublimite" },
  { value: "3", label: "3 - Regime Normal" },
];

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", 
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", 
  "SP", "SE", "TO"
];

const emitterSchema = z.object({
  cnpj: z.string().min(14, "CNPJ é obrigatório (mínimo 14 caracteres)"),
  razao_social: z.string().min(1, "Razão social é obrigatória"),
  nome_fantasia: z.string().optional(),
  ie: z.string().min(1, "Inscrição Estadual é obrigatória"),
  crt: z.string().min(1, "CRT é obrigatório"),
  uf: z.string().length(2, "UF deve ter 2 caracteres"),
  city: z.string().min(1, "Município é obrigatório"),
  codigo_municipio: z.string().min(7, "Código IBGE é obrigatório (7 dígitos)"),
  cep: z.string().min(8, "CEP é obrigatório"),
  address: z.string().min(1, "Logradouro é obrigatório"),
  numero_endereco: z.string().min(1, "Número é obrigatório"),
  bairro: z.string().min(1, "Bairro é obrigatório"),
  complemento_endereco: z.string().optional(),
  phone: z.string().optional(),
});

type EmitterFormData = z.infer<typeof emitterSchema>;

export function EmitterDataForm() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [saving, setSaving] = useState(false);
  const [searchingCep, setSearchingCep] = useState(false);

  const form = useForm<EmitterFormData>({
    resolver: zodResolver(emitterSchema),
    defaultValues: {
      cnpj: "",
      razao_social: "",
      nome_fantasia: "",
      ie: "",
      crt: "1",
      uf: "",
      city: "",
      codigo_municipio: "",
      cep: "",
      address: "",
      numero_endereco: "",
      bairro: "",
      complemento_endereco: "",
      phone: "",
    },
  });

  // Load restaurant data into form
  useEffect(() => {
    if (activeRestaurant) {
      const restaurant = activeRestaurant as any;
      form.reset({
        cnpj: restaurant.cnpj || "",
        razao_social: restaurant.razao_social || "",
        nome_fantasia: restaurant.nome_fantasia || activeRestaurant.name || "",
        ie: restaurant.ie || "",
        crt: restaurant.crt?.toString() || "1",
        uf: restaurant.uf || "",
        city: restaurant.city || "",
        codigo_municipio: restaurant.codigo_municipio || "",
        cep: restaurant.cep || "",
        address: restaurant.address || "",
        numero_endereco: restaurant.numero_endereco || "",
        bairro: restaurant.bairro || "",
        complemento_endereco: restaurant.complemento_endereco || "",
        phone: activeRestaurant.phone || "",
      });
    }
  }, [activeRestaurant, form]);

  const handleSearchCep = async () => {
    const cep = form.getValues("cep").replace(/\D/g, "");
    if (cep.length !== 8) {
      toast.error("CEP inválido", { description: "O CEP deve ter 8 dígitos" });
      return;
    }

    setSearchingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();

      if (data.erro) {
        toast.error("CEP não encontrado");
        return;
      }

      form.setValue("address", data.logradouro || "");
      form.setValue("bairro", data.bairro || "");
      form.setValue("city", data.localidade || "");
      form.setValue("uf", data.uf || "");
      form.setValue("codigo_municipio", data.ibge || "");
      toast.success("Endereço preenchido!");
    } catch {
      toast.error("Erro ao buscar CEP");
    } finally {
      setSearchingCep(false);
    }
  };

  const onSubmit = async (data: EmitterFormData) => {
    if (!activeRestaurant?.id) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("restaurants")
        .update({
          cnpj: data.cnpj.replace(/\D/g, ""),
          razao_social: data.razao_social,
          nome_fantasia: data.nome_fantasia || null,
          ie: data.ie.replace(/\D/g, ""),
          crt: parseInt(data.crt),
          uf: data.uf,
          city: data.city,
          codigo_municipio: data.codigo_municipio,
          cep: data.cep.replace(/\D/g, ""),
          address: data.address,
          numero_endereco: data.numero_endereco,
          bairro: data.bairro,
          complemento_endereco: data.complemento_endereco || null,
          phone: data.phone || null,
        })
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      toast.success("Dados fiscais salvos com sucesso!");
      await refetch();
    } catch (error) {
      console.error("Erro ao salvar dados fiscais:", error);
      toast.error("Erro ao salvar dados fiscais");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados da Empresa */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Building2 className="h-4 w-4" />
            Dados da Empresa
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="cnpj"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CNPJ *</FormLabel>
                  <FormControl>
                    <Input placeholder="00.000.000/0001-00" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="ie"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Inscrição Estadual *</FormLabel>
                  <FormControl>
                    <Input placeholder="123456789012" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="razao_social"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Razão Social *</FormLabel>
                <FormControl>
                  <Input placeholder="EMPRESA EXEMPLO LTDA" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="nome_fantasia"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome Fantasia</FormLabel>
                  <FormControl>
                    <Input placeholder="Meu Restaurante" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="crt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CRT *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {CRT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Endereço */}
        <div className="space-y-4">
          <div className="text-sm font-medium text-muted-foreground">
            Endereço
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <FormField
              control={form.control}
              name="cep"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CEP *</FormLabel>
                  <div className="flex gap-2">
                    <FormControl>
                      <Input placeholder="00000-000" {...field} />
                    </FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleSearchCep}
                      disabled={searchingCep}
                    >
                      {searchingCep ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="uf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>UF *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {UF_OPTIONS.map((uf) => (
                        <SelectItem key={uf} value={uf}>
                          {uf}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="codigo_municipio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cód. IBGE *</FormLabel>
                  <FormControl>
                    <Input placeholder="3550308" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Município *</FormLabel>
                <FormControl>
                  <Input placeholder="São Paulo" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-4">
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Logradouro *</FormLabel>
                  <FormControl>
                    <Input placeholder="Av. Paulista" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="numero_endereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Número *</FormLabel>
                  <FormControl>
                    <Input placeholder="1000" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="complemento_endereco"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complemento</FormLabel>
                  <FormControl>
                    <Input placeholder="Sala 101" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="bairro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bairro *</FormLabel>
                  <FormControl>
                    <Input placeholder="Centro" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Telefone</FormLabel>
                  <FormControl>
                    <Input placeholder="(11) 99999-9999" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? "Salvando..." : "Salvar Dados Fiscais"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
